import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatNumber } from '@progress/kendo-intl';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import cn from 'clsx';

import styles from './style.module.scss';
import { ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';

export interface MultiValueDataItem {
    valueAfter: number;
    valueBefore: number;
    colourAfter: string;
    colourBefore: string;
    changeDirection?: ScoreDirection;
}

export interface FormatCellProps {
    numberFormat?: string;
}

export interface MultiValueCellProps<Data = MultiValueDataItem> extends GridCustomCellProps {
    dataItem: Data;
    tooltipText?: string;
    highlighted?: boolean;
    showBlankText?: boolean;
    width?: number;
    isVirtualized?: boolean;
}

const getHighlightClass = (color: string) => {
    switch (color) {
        case 'Red':
            return styles.highlightedRed;
        case 'Green':
            return styles.highlightedGreen;
        case 'Amber':
            return styles.highlightedAmber;
        case 'Purple':
            return styles.highlightedPurple;
        default:
            return '';
    }
};

const { BasicCell } = createGridCustomCells();

export const MultiValueCell = ({
    dataItem,
    highlighted,
    numberFormat,
    showBlankText = true,
    width,
    ...restProps
}: MultiValueCellProps & FormatCellProps) => {
    const { changeDirection, valueAfter, valueBefore, colourAfter, colourBefore } = dataItem;
    return (
        <BasicCell
            {...restProps}
            dataItem={dataItem}
            tdProps={{
                ...restProps.tdProps,
                className: `${restProps.className} k-align-items-center k-justify-content-center`,
                style: {
                    ...restProps.tdProps?.style,
                    ...(!restProps.isVirtualized ? { width: '90%' } : {}),
                },
            }}
        >
            <div className="k-display-flex k-align-items-center !k-justify-content-center">
                {valueBefore == null && valueAfter == null && showBlankText ? (
                    <div className={cn(styles.cellContent)}>
                        <span>N/A</span>
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                visibility: valueBefore == null ? 'hidden' : 'visible',
                            }}
                            className={cn(styles.cellContent, {
                                [`${styles.highlightedContainer}`]: highlighted,
                            })}
                        >
                            <span
                                style={width ? { width, textAlign: 'right' } : undefined}
                                className={cn({
                                    [getHighlightClass(HC_RAG_NAMES[+colourBefore])]: highlighted,
                                })}
                            >
                                {valueBefore !== undefined && valueBefore !== null
                                    ? numberFormat
                                        ? formatNumber(valueBefore, numberFormat)
                                        : valueBefore
                                    : ''}
                            </span>
                        </div>
                        <span
                            className="k-display-flex k-align-items-center !k-justify-content-center"
                            style={{
                                width: '5%',
                                minWidth: '5%',
                                visibility: valueBefore == null || valueAfter == null ? 'hidden' : 'visible',
                            }}
                        >
                            {dataItem.changeDirection !== undefined &&
                            dataItem.changeDirection !== ScoreDirection.Matching ? (
                                <FontAwesomeIcon
                                    icon={['fass', 'triangle']}
                                    className={cn({
                                        [styles.sortAscIcon]: changeDirection === ScoreDirection.Upper,
                                        [`${styles.sortDescIcon} k-rotate-180`]:
                                            changeDirection === ScoreDirection.Lower,
                                    })}
                                />
                            ) : (
                                <FontAwesomeIcon icon={['far', 'hyphen']} className={cn(styles.sortAscIcon)} />
                            )}
                        </span>
                        <div
                            style={{
                                visibility: valueAfter == null ? 'hidden' : 'visible',
                            }}
                            className={cn(styles.cellContent, {
                                [styles.highlightedContainer]: highlighted,
                            })}
                        >
                            <span
                                style={width ? { width, textAlign: 'right' } : undefined}
                                className={cn({
                                    [getHighlightClass(HC_RAG_NAMES[+colourAfter])]: highlighted,
                                })}
                            >
                                {valueAfter !== undefined && valueAfter !== null
                                    ? numberFormat
                                        ? formatNumber(valueAfter, numberFormat)
                                        : valueAfter
                                    : ''}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </BasicCell>
    );
};
